import { useEffect, useMemo, useRef, useState } from "react";
import eventGroupPhoto from "../../../../assets/images/events/BannerHome.png";
import AppLayout from "../../../../shared/components/AppLayout";
import Button from "../../../../shared/components/Button";
import Spinner from "../../../../shared/components/Spinner";
import { siteTexts } from "../../../../shared/i18n/siteTexts";
import { formatEventDateRangeLabel } from "../../../../shared/utils/dateTime";
import { listPublicChallenges } from "../../../challenges/services/challengeAdminService";
import {
  ChallengeCategoryCode,
  ChallengeHistoryItem,
} from "../../../challenges/types/challengeAdmin";
import { resolveChallengeCoverImage } from "../../../challenges/utils/challengeCoverImage";
import {
  categoryLabel,
  challengeCategorySummary,
} from "../../../challenges/utils/challengeLabels";
import { listClubs } from "../../../clubs/shared/services/clubsService";
import type { Club } from "../../../clubs/shared/types/club";
import { useSiteContent } from "../../shared/hooks/useSiteContent";
import "./HomePage.css";

type HomeEventCategoryBadge = {
  key: string;
  label: string;
  tone: "male" | "female" | "mixed" | "free" | "muted";
};

function isChallengeCategoryCode(value: string): value is ChallengeCategoryCode {
  return (
    value === "7a" ||
    value === "6a" ||
    value === "5a" ||
    value === "4a" ||
    value === "3a" ||
    value === "2a" ||
    value === "open" ||
    value === "free"
  );
}

function compactCategoryCodeLabel(value: string) {
  return isChallengeCategoryCode(value) ? categoryLabel(value) : value;
}

function compactTournamentCategoryLabel(value: string) {
  return value
    .replace(/\bMasculina\b/gi, "Masc")
    .replace(/\bMasculino\b/gi, "Masc")
    .replace(/\bFeminina\b/gi, "Fem")
    .replace(/\bFeminino\b/gi, "Fem")
    .replace(/\bMista\b/gi, "Mista")
    .replace(/\s+/g, " ")
    .trim();
}

function resolveEventCategoryBadgeTone(
  category: NonNullable<ChallengeHistoryItem["registrationCategories"]>[number],
): HomeEventCategoryBadge["tone"] {
  const gender = String(category.categoryGender || "").trim().toLowerCase();
  const categoryMale = String(category.categoryMale || "").trim().toLowerCase();
  const categoryFemale = String(category.categoryFemale || "")
    .trim()
    .toLowerCase();

  if (categoryMale === "open" || categoryFemale === "open") return "free";
  if (gender === "male") return "male";
  if (gender === "female") return "female";
  if (gender === "mixed") return "mixed";
  if (gender === "open") return "free";
  return "muted";
}

function resolveChallengeCategoryBadgeTone(
  item: ChallengeHistoryItem,
): HomeEventCategoryBadge["tone"] {
  const category = String(item.category || "").trim().toLowerCase();
  const categoryMale = String(item.categoryMale || "").trim().toLowerCase();
  const categoryFemale = String(item.categoryFemale || "").trim().toLowerCase();

  if (
    category === "free" ||
    category === "open" ||
    categoryMale === "free" ||
    categoryMale === "open" ||
    categoryFemale === "free" ||
    categoryFemale === "open"
  ) {
    return "free";
  }
  if (item.categoryGender === "male") return "male";
  if (item.categoryGender === "female") return "female";
  if (item.categoryGender === "mixed") return "mixed";
  if (item.categoryGender === "open") return "free";
  return "muted";
}

function eventCategoryFallbackLabel(
  category: NonNullable<ChallengeHistoryItem["registrationCategories"]>[number],
) {
  const gender = String(category.categoryGender || "").trim().toLowerCase();
  const male = String(category.categoryMale || "").trim();
  const female = String(category.categoryFemale || "").trim();

  if (male === "open" || female === "open") return "Livre";
  if (gender === "male" && male) return `${compactCategoryCodeLabel(male)} Masc`;
  if (gender === "female" && female) return `${compactCategoryCodeLabel(female)} Fem`;
  if (gender === "mixed" || gender === "open") {
    const genderLabel =
      gender === "open" ? "Livre" : "Mista";
    if (male && female && male !== female) {
      return `${compactCategoryCodeLabel(male)}/${compactCategoryCodeLabel(female)} ${genderLabel}`;
    }
    if (male || female) return `${compactCategoryCodeLabel(male || female)} ${genderLabel}`;
    return genderLabel;
  }
  return "Categoria";
}

export function homeEventCategoryBadges(item: ChallengeHistoryItem) {
  const showsRegistrationCategories =
    item.challengeType === "tournament" || item.challengeType === "challenge";
  if (!showsRegistrationCategories) return [];
  const categories = Array.isArray(item.registrationCategories)
    ? item.registrationCategories
    : [];

  return categories
    .map((category, index) => {
      const rawLabel =
        String(category.categoryName || "").trim() ||
        eventCategoryFallbackLabel(category);
      const label = compactTournamentCategoryLabel(rawLabel);
      if (!label) return null;
      return {
        key: String(category.categoryKey || "").trim() || `${label}-${index}`,
        label,
        tone: resolveEventCategoryBadgeTone(category),
      } satisfies HomeEventCategoryBadge;
    })
    .filter(Boolean) as HomeEventCategoryBadge[];
}

export function resolveHomeEventCategoryBadges(item: ChallengeHistoryItem) {
  const registrationBadges = homeEventCategoryBadges(item);
  if (registrationBadges.length > 0) return registrationBadges;

  const fallbackLabel = compactTournamentCategoryLabel(
    challengeCategorySummary(item),
  );
  if (!fallbackLabel) return [];

  return [
    {
      key: `summary-${item.id || item.challengeType || "event"}`,
      label: fallbackLabel,
      tone: resolveChallengeCategoryBadgeTone(item),
    } satisfies HomeEventCategoryBadge,
  ];
}

function parseHomeEventTime(value?: string | null) {
  const parsed = value ? new Date(value) : null;
  if (!parsed || Number.isNaN(parsed.getTime())) return null;
  return parsed.getTime();
}

function homeEventEndTime(item: ChallengeHistoryItem) {
  return (
    parseHomeEventTime(item.scheduledEndAt) ??
    parseHomeEventTime(item.scheduledAt)
  );
}

export function isUpcomingHomeChallenge(
  item: ChallengeHistoryItem,
  now: Date = new Date(),
) {
  const status = String(item.status || "").trim().toLowerCase();
  if (status === "finished" || status === "canceled" || status === "cancelled") {
    return false;
  }

  const nowTime = now.getTime();
  const endTime = homeEventEndTime(item);
  if (!Number.isFinite(nowTime) || endTime === null) return true;

  return endTime >= nowTime;
}

export default function HomePage() {
  const { content } = useSiteContent();
  const aboutSectionRef = useRef<HTMLElement | null>(null);
  const eventsSectionRef = useRef<HTMLElement | null>(null);
  const eventsMarqueeRef = useRef<HTMLDivElement | null>(null);
  const [aboutVisible, setAboutVisible] = useState(false);
  const [isNarrowScreen, setIsNarrowScreen] = useState(false);
  const [eventsManualMode, setEventsManualMode] = useState(false);
  const [pendingChallenges, setPendingChallenges] = useState<
    ChallengeHistoryItem[]
  >([]);
  const [homeClubs, setHomeClubs] = useState<Club[]>([]);
  const [homeClubsVisibleCount, setHomeClubsVisibleCount] = useState(2);
  const [eventsLoading, setEventsLoading] = useState(true);

  useEffect(() => {
    if (
      typeof window === "undefined" ||
      typeof window.matchMedia !== "function"
    )
      return;
    const media = window.matchMedia("(max-width: 900px)");
    const sync = () => setIsNarrowScreen(Boolean(media.matches));
    sync();
    // Safari fallback for older addListener/removeListener is not necessary for our target.
    media.addEventListener("change", sync);
    return () => media.removeEventListener("change", sync);
  }, []);

  useEffect(() => {
    setHomeClubsVisibleCount(isNarrowScreen ? 2 : 4);
  }, [isNarrowScreen]);

  useEffect(() => {
    const section = aboutSectionRef.current;
    if (!section) return;
    if (typeof window === "undefined") return;

    let observer: IntersectionObserver | null = null;
    let rafId: number | null = null;
    let cleanedUp = false;

    const cleanup = () => {
      if (cleanedUp) return;
      cleanedUp = true;
      if (observer) observer.disconnect();
      window.removeEventListener("scroll", scheduleCheck);
      window.removeEventListener("resize", scheduleCheck);
      if (rafId !== null) window.cancelAnimationFrame(rafId);
    };

    const checkVisible = () => {
      rafId = null;
      const rect = section.getBoundingClientRect();
      const viewportHeight =
        window.innerHeight || document.documentElement.clientHeight || 0;

      // Revele uma vez quando a seção estiver razoavelmente no viewport.
      const isVisible =
        rect.top < viewportHeight * 0.85 && rect.bottom > viewportHeight * 0.15;

      if (isVisible) {
        setAboutVisible(true);
        cleanup();
      }
    };

    const scheduleCheck = () => {
      if (rafId !== null) return;
      rafId = window.requestAnimationFrame(checkVisible);
    };

    if (typeof IntersectionObserver === "function") {
      observer = new IntersectionObserver(
        (entries) => {
          if (entries.some((entry) => entry.isIntersecting)) {
            setAboutVisible(true);
            cleanup();
          }
        },
        {
          threshold: 0.25,
          rootMargin: "0px 0px -10% 0px",
        },
      );

      observer.observe(section);
    } else {
      setAboutVisible(true);
      return;
    }

    window.addEventListener("scroll", scheduleCheck, { passive: true });
    window.addEventListener("resize", scheduleCheck);
    scheduleCheck();

    return cleanup;
  }, []);

  useEffect(() => {
    let cancelled = false;
    setEventsLoading(true);
    void (async () => {
      try {
        const challenges = await listPublicChallenges({ status: "pending" });
        if (cancelled) return;
        const sorted = [...(challenges || [])]
          .filter((item) => isUpcomingHomeChallenge(item))
          .sort((a, b) => {
            const aTime = new Date(
              a.scheduledAt || a.createdAt || "",
            ).getTime();
            const bTime = new Date(
              b.scheduledAt || b.createdAt || "",
            ).getTime();
            return aTime - bTime;
          });
        setPendingChallenges(sorted);
      } catch (_err) {
        if (cancelled) return;
        setPendingChallenges([]);
      } finally {
        if (!cancelled) setEventsLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    void listClubs({})
      .then((response) => {
        if (cancelled) return;
        setHomeClubs(response.clubs || []);
      })
      .catch(() => {
        if (cancelled) return;
        setHomeClubs([]);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const heroImage = eventGroupPhoto;
  const heroKickerBadges = siteTexts.home.heroKicker
    .split("•")
    .map((item) => item.trim())
    .filter(Boolean);
  const visibleEvents = useMemo(
    () =>
      pendingChallenges.map((item, originalIndex) => ({
        originalIndex,
        event: (() => {
          const title = String(
            item.challengeName || item.groupName || `Desafio #${item.id}`,
          ).trim();
          const categoryBadges = resolveHomeEventCategoryBadges(item);
          return {
            id: item.id,
            title,
            date: formatEventDateRangeLabel(
              item.scheduledAt,
              item.scheduledEndAt,
            ),
            address: String(item.address || "").trim(),
            category: challengeCategorySummary(item),
            categoryBadges,
            hiddenCategoriesCount:
              categoryBadges.length > 0
                ? Math.max(0, categoryBadges.length - 2)
                : 0,
            imageUrl: resolveChallengeCoverImage(item, content.events),
            href: `#/desafios/detalhe/${item.id}`,
          };
        })(),
      })),
    [content.events, pendingChallenges],
  );
  const shouldAnimateEvents = visibleEvents.length >= 3 && !isNarrowScreen;
  const shouldShowEventNav = shouldAnimateEvents;
  const movingEvents =
    shouldAnimateEvents && !eventsManualMode
      ? [...visibleEvents, ...visibleEvents]
      : visibleEvents;
  const visibleHomeClubs = useMemo(() => {
    const limit = isNarrowScreen ? homeClubsVisibleCount : 4;
    return homeClubs.slice(0, limit);
  }, [homeClubs, homeClubsVisibleCount, isNarrowScreen]);
  const hasMoreHomeClubs = homeClubs.length > visibleHomeClubs.length;
  const shouldShowHomeClubs = homeClubs.length > 1;
  const aboutSummary = siteTexts.home.summary;
  const aboutItems = useMemo(() => {
    const parsed = siteTexts.home.aboutFallbackItems.map((item) => {
      const text = String(item || "").trim();
      if (text.includes("|")) {
        const [title, description] = text.split("|");
        return {
          title: title.trim(),
          description: String(description || "").trim(),
        };
      }
      if (text.includes(" - ")) {
        const [title, description] = text.split(" - ");
        return {
          title: title.trim(),
          description: String(description || "").trim(),
        };
      }
      return { title: text, description: "" };
    });

    const fallbackItems = [
      {
        title: siteTexts.home.aboutFallbackItems[0],
        description: "",
      },
      {
        title: siteTexts.home.aboutFallbackItems[1],
        description: "",
      },
      {
        title: siteTexts.home.aboutFallbackItems[2],
        description: "",
      },
      {
        title: siteTexts.home.aboutFallbackItems[3],
        description: "",
      },
    ];

    const merged = parsed.length > 0 ? [...parsed] : [...fallbackItems];
    while (merged.length < 4) {
      merged.push(fallbackItems[merged.length]);
    }
    return merged.slice(0, 4);
  }, []);

  const moveEvents = (direction: "left" | "right") => {
    const scrollOnce = () => {
      const container = eventsMarqueeRef.current;
      if (!container) return;
      const firstCard =
        container.querySelector<HTMLElement>(".home-event-card");
      const step = (firstCard?.offsetWidth || 320) + 16;
      container.scrollBy({
        left: direction === "right" ? step : -step,
        behavior: "smooth",
      });
    };

    if (!eventsManualMode) {
      setEventsManualMode(true);
      if (typeof window !== "undefined") {
        window.requestAnimationFrame(() => {
          window.requestAnimationFrame(scrollOnce);
        });
      }
      return;
    }

    scrollOnce();
  };

  const scrollToEventsSection = () => {
    eventsSectionRef.current?.scrollIntoView({
      behavior: "smooth",
      block: "start",
    });
  };

  return (
    <AppLayout>
      <div className="home-v2">
        <section
          className="home-hero"
          style={{ backgroundImage: `url(${heroImage})` }}
        >
          <div className="home-hero-overlay" />

          <div className="home-hero-content">
            <div className="home-kicker" aria-label={siteTexts.home.heroKicker}>
              {heroKickerBadges.map((badge) => (
                <span key={badge}>{badge}</span>
              ))}
            </div>
            <h1>{siteTexts.home.hero.title}</h1>
            <p>{siteTexts.home.hero.subtitle}</p>
            <div className="home-hero-actions">
              <Button variant="primary" type="button" onClick={scrollToEventsSection}>
                {siteTexts.home.hero.ctaLabel || siteTexts.home.heroFallbackCta}
              </Button>
              <Button variant="pill" href="#/ranking/tnp">
                Ver ranking
              </Button>
            </div>
          </div>
        </section>

        <section ref={aboutSectionRef} className="home-block home-about-block">
          <div className="home-section-header home-about-header">
            <h2>{siteTexts.home.aboutFallbackTitle}</h2>
            <p className="home-about-description">{aboutSummary}</p>
          </div>
          <div className="home-adv-grid">
            {aboutItems.map((item, index) => (
              <article
                key={`${item.title}-${index}`}
                className={`home-adv-card${aboutVisible ? " is-visible" : ""}`}
                style={{ transitionDelay: `${index * 120}ms` }}
              >
                <span className="home-adv-index">
                  {String(index + 1).padStart(2, "0")}
                </span>
                <h3>{item.title}</h3>
                {item.description && <p>{item.description}</p>}
              </article>
            ))}
          </div>
        </section>

        {shouldShowHomeClubs && (
          <section className="home-block home-clubs-block">
            <div className="home-section-header home-clubs-header">
              <div>
                <h2>{siteTexts.home.clubs.title}</h2>
                <p>{siteTexts.home.clubs.subtitle}</p>
              </div>
              <Button type="button" variant="pill" href="#/clubes">
                {siteTexts.home.clubs.viewAll}
              </Button>
            </div>
            <div className="home-clubs-grid">
              {visibleHomeClubs.map((club) => (
                <a
                  className="home-club-card home-club-card--compact"
                  key={club.id}
                  href={`#/clubes/${club.slug}`}
                >
                  <span className="home-club-logo-frame home-club-logo-frame--inline">
                    <img src={club.logoUrl} alt={club.name} />
                  </span>
                  <span className="home-club-body home-club-body--compact">
                    <span className="home-showcase-tag">
                      {siteTexts.home.clubs.nearbyTag}
                    </span>
                    <strong>{club.name}</strong>
                  </span>
                </a>
              ))}
            </div>
            {isNarrowScreen && hasMoreHomeClubs ? (
              <div className="home-clubs-more">
                <Button
                  type="button"
                  variant="pill"
                  onClick={() =>
                    setHomeClubsVisibleCount((current) =>
                      Math.min(current + 2, homeClubs.length),
                    )
                  }
                >
                  {siteTexts.home.clubs.viewMore}
                </Button>
              </div>
            ) : null}
          </section>
        )}

        <section ref={eventsSectionRef} className="home-block">
          <div className="home-section-header">
            <h2>{siteTexts.home.events.title}</h2>
            <p>{siteTexts.home.events.subtitle}</p>
          </div>
          {eventsLoading && <Spinner label={siteTexts.home.loadingContent} />}
          {!eventsLoading && visibleEvents.length === 0 && (
            <p className="status-message">{siteTexts.home.events.empty}</p>
          )}
          {!eventsLoading && visibleEvents.length > 0 && (
            <>
              {shouldShowEventNav && (
                <div
                  className="home-events-nav"
                  aria-label={siteTexts.home.events.navAria}
                >
                  <button
                    type="button"
                    className="home-events-nav-btn"
                    onClick={() => moveEvents("left")}
                    aria-label={siteTexts.home.events.moveLeft}
                  >
                    ←
                  </button>
                  <button
                    type="button"
                    className="home-events-nav-btn"
                    onClick={() => moveEvents("right")}
                    aria-label={siteTexts.home.events.moveRight}
                  >
                    →
                  </button>
                </div>
              )}
              <div
                ref={eventsMarqueeRef}
                className={`home-events-marquee${eventsManualMode ? " is-manual" : ""}`}
              >
                <div
                  className={`home-events-track${
                    shouldAnimateEvents && !eventsManualMode
                      ? " is-animated"
                      : shouldAnimateEvents && eventsManualMode
                        ? " is-static-scroll"
                        : " is-static"
                  }`}
                >
                  {movingEvents.map((item, index) => {
                    const event = item.event;
                    return (
                      <a
                        key={`${event.id}-${index}`}
                        className="home-event-card"
                        href={event.href}
                      >
                        <div
                          className="home-event-media"
                          style={{
                            backgroundImage: event.imageUrl
                              ? `url(${event.imageUrl})`
                              : undefined,
                          }}
                        />
                        <div className="home-event-body">
                          <span className="home-showcase-tag">
                            {event.date || siteTexts.home.events.fallbackTag}
                          </span>
                          <div className="home-event-categories">
                            <div
                              className="home-event-category-badges"
                              aria-label="Categorias do evento"
                            >
                              {(event.categoryBadges.length > 0
                                ? event.categoryBadges.slice(0, 2)
                                : [
                                    {
                                      key: "fallback",
                                      label: "Categorias a definir",
                                      tone: "muted" as const,
                                    },
                                  ]
                              ).map((category) => (
                                <span
                                  key={category.key}
                                  className={`home-event-category-badge is-${category.tone}`}
                                >
                                  {category.label}
                                </span>
                              ))}
                            </div>
                            {event.hiddenCategoriesCount > 0 && (
                              <span className="home-event-category-more">
                                +{event.hiddenCategoriesCount} categorias
                              </span>
                            )}
                          </div>
                          <h3>{event.title}</h3>
                          <p>
                            {event.address ||
                              siteTexts.home.events.fallbackAddress}
                          </p>
                        </div>
                      </a>
                    );
                  })}
                </div>
              </div>
            </>
          )}
        </section>

        {/* Seção de destaques/loja temporariamente ocultada */}

        {/* <section className="home-block">
          <div className="home-section-header">
            <h2>Instagram</h2>
            <p>@todosnopadel • últimas publicações</p>
          </div>
          <div className="home-insta-grid">
            {content.instagram.map((item, index) => (
              <a
                key={`${item.link}-${index}`}
                href={item.link || "https://instagram.com/todosnopadel"}
                className="home-insta-card"
                target="_blank"
                rel="noreferrer"
              >
                <div
                  className="home-insta-media"
                  style={{
                    backgroundImage: `url(${
                      item.imageUrl ||
                      instagramFallbackImages[index % instagramFallbackImages.length]
                    })`,
                  }}
                >
                  {!item.imageUrl && <span>@todosnopadel</span>}
                </div>
              </a>
            ))}
          </div>
        </section> */}
      </div>
    </AppLayout>
  );
}
